import React, { useState } from 'react';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Tabs } from '../../../design-system/Tabs';
import { Modal } from '../../../design-system/Modal';
import { Input, Textarea } from '../../../design-system/Input';
import {
  FlaskConical,
  Database,
  BookOpen,
  FileText,
  Plus,
  Sparkles
} from 'lucide-react';

export const ResearchView = () => {
  const [activeTab, setActiveTab] = useState('hypotheses');
  const [isNewHypModalOpen, setIsNewHypModalOpen] = useState(false);

  const [hypTitle, setHypTitle] = useState('');
  const [hypStatement, setHypStatement] = useState('');
  const [hypLead, setHypLead] = useState('');

  const tabs = [
    { id: 'hypotheses', label: 'Hypotheses & Questions', icon: <FlaskConical size={15} /> },
    { id: 'experiments', label: 'Trials & Datasets', icon: <Database size={15} /> },
    { id: 'literature', label: 'Literature & Citations', icon: <BookOpen size={15} /> },
    { id: 'publications', label: 'Findings & Publications', icon: <FileText size={15} /> }
  ];

  const [hypotheses, setHypotheses] = useState([
    {
      id: 'HYP-01',
      title: 'Optimistic UI Latency Thresholds',
      statement: 'Applying low-latency optimistic UI reconciliation reduces perceived task movement friction by >40% across high-frequency boards.',
      status: 'Validated',
      confidence: '95% (p < 0.001)',
      sampleSize: 'N = 1,420 actions',
      lead: 'Rohith Prince'
    },
    {
      id: 'HYP-02',
      title: 'Neomorphic Dual-Shadow Contrast & Eye Strain',
      statement: 'Neomorphic dual-shadow contrast guardrails prevent eye fatigue in dark workspace sessions compared to high-contrast borders.',
      status: 'In Experimentation',
      confidence: '78%',
      sampleSize: 'N = 380 sessions',
      lead: 'Elena Rostov'
    }
  ]);

  const experiments = [
    { id: 'EXP-101', name: 'A/B Latency Perception Trial #4', dataset: 'ds_latency_benchmark_2026.parquet (42MB)', result: 'Δ -180ms perceived latency', status: 'Completed' },
    { id: 'EXP-102', name: 'Contrast Calibration Dark Mode Cohort B', dataset: 'ds_eye_tracking_survey.csv (12MB)', result: '92% positive rating', status: 'Active Run' }
  ];

  const literature = [
    { title: 'The Neurological Impact of Micro-Interactions in Work Software', authors: 'Vance, M. et al.', year: '2025', journal: 'ACM Transactions on CHI', doi: '10.1145/3411764' },
    { title: 'Visual Hierarchy and Neomorphic Tactile Surfaces', authors: 'Chen, L. & Prince, R.', year: '2026', journal: 'IEEE Computer Graphics', doi: '10.1109/MCG.2026' }
  ];

  const handleAddHypothesis = (e) => {
    e?.preventDefault();
    if (!hypStatement.trim()) return;

    setHypotheses([
      ...hypotheses,
      {
        id: `HYP-${String(hypotheses.length + 1).padStart(2, '0')}`,
        title: hypTitle.trim() || 'New Hypothesis',
        statement: hypStatement.trim(),
        status: 'Formulating',
        confidence: 'Pending Trials',
        sampleSize: 'N = 0',
        lead: hypLead.trim() || 'Principal Researcher'
      }
    ]);
    setHypTitle('');
    setHypStatement('');
    setHypLead('');
    setIsNewHypModalOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="info" size="sm">Scientific Research Methodology</Badge>
            <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Research & Hypotheses Suite</h2>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Empirical inquiry, experiment trial runs, literature synthesis, and dataset provenance
          </p>
        </div>

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* TAB 1: HYPOTHESES */}
      {activeTab === 'hypotheses' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600' }}>Active Scientific Inquiries ({hypotheses.length})</span>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setIsNewHypModalOpen(true)}>
              New Hypothesis
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {hypotheses.map((h) => (
              <Card key={h.id} padding="22px">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Badge variant="info" size="xs">{h.id}</Badge>
                    <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-primary)' }}>{h.title}</span>
                  </div>
                  <Badge variant={h.status === 'Validated' ? 'success' : 'teal'} size="sm">
                    {h.status} • {h.confidence}
                  </Badge>
                </div>

                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '14px' }}>
                  "{h.statement}"
                </p>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'var(--text-muted)', paddingTop: '10px', borderTop: '1px solid var(--border-divider)' }}>
                  <span>Principal Investigator: <strong>{h.lead}</strong></span>
                  <span>Sample Size: <strong>{h.sampleSize}</strong></span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: TRIALS & DATASETS */}
      {activeTab === 'experiments' && (
        <Card padding="24px">
          <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '14px' }}>Experiment Trials & Dataset Provenance</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {experiments.map((exp) => (
              <div key={exp.id} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Database size={20} color="var(--accent-teal)" />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '700' }}>{exp.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Dataset: {exp.dataset} • {exp.result}</div>
                  </div>
                </div>
                <Badge variant={exp.status === 'Completed' ? 'success' : 'teal'} size="xs">{exp.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 3: LITERATURE */}
      {activeTab === 'literature' && (
        <Card padding="24px">
          <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '14px' }}>Literature References & DOI Citations</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {literature.map((lit, i) => (
              <div key={i} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--border-subtle)' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700' }}>{lit.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{lit.authors} ({lit.year}) • <em>{lit.journal}</em></div>
                </div>
                <Badge variant="recessed" size="xs">DOI: {lit.doi}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 4: PUBLICATIONS */}
      {activeTab === 'publications' && (
        <Card padding="24px">
          <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '12px' }}>Manuscript Drafts & Publications</h3>
          <div style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700' }}>Forma Architecture: Low-Latency Modular Work Management</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Target: Communications of the ACM 2026 • Status: Final Draft Review</div>
            </div>
            <Badge variant="teal" size="sm">Peer-Review Submitted</Badge>
          </div>
        </Card>
      )}

      {/* NEW HYPOTHESIS MODAL */}
      <Modal
        isOpen={isNewHypModalOpen}
        onClose={() => setIsNewHypModalOpen(false)}
        title="Formulate Research Hypothesis"
        subtitle="Specify verifiable inquiry statement and sample design"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewHypModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddHypothesis} icon={<Sparkles size={14} />}>Save Hypothesis</Button>
          </>
        }
      >
        <form onSubmit={handleAddHypothesis} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Inquiry Title *"
            placeholder="e.g., Optimistic UI Synchronization"
            value={hypTitle}
            onChange={(e) => setHypTitle(e.target.value)}
            required
            autoFocus
          />
          <Textarea
            label="Hypothesis Statement *"
            placeholder="e.g., If [condition] then [observed outcome] because [theoretical reasoning]..."
            value={hypStatement}
            onChange={(e) => setHypStatement(e.target.value)}
            rows={3}
            required
          />
          <Input
            label="Principal Investigator"
            placeholder="e.g., Rohith Prince"
            value={hypLead}
            onChange={(e) => setHypLead(e.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
};
